import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, Button, FormControl,
  InputLabel, Select, MenuItem, TextField, Divider,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Chip, Alert, CircularProgress, Stack,
  Stepper, Step, StepLabel, Tooltip
} from '@mui/material';
import {
  Download, CloudUpload, CheckCircle, Cancel, Visibility, Save
} from '@mui/icons-material';
import api from '../../api/axios';
import toast from 'react-hot-toast';

/* ──────────────────────────────────────────────────────────
   HELPERS
────────────────────────────────────────────────────────── */
const getTodayString = () => {
  const now = new Date();
  return now.toISOString().split('T')[0]; // Returns "YYYY-MM-DD"
};

const statusColor = (status) => {
  const map = {
    P: 'success', A: 'error', L: 'info',
    H: 'warning', 'W/O': 'default', HD: 'warning'
  };
  return map[status] || 'default';
};

/* ──────────────────────────────────────────────────────────
   COMPONENT
────────────────────────────────────────────────────────── */
const STEPS = ['Select Parameters', 'Download Template', 'Upload Filled Sheet', 'Preview & Confirm'];

const HRManualAttendanceUpload = () => {

  /* ── State ── */
  const [activeStep, setActiveStep] = useState(0);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  
  // Dynamic Flexible Date Ranges supporting custom enterprise payroll boundaries
  const [startDate, setStartDate] = useState(getTodayString());
  const [endDate, setEndDate] = useState(getTodayString());

  const [file, setFile] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [preview, setPreview] = useState(null);  
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [saveResult, setSaveResult] = useState(null);

  const fileInputRef = useRef();

  /* ── Load branches ── */
  useEffect(() => {
    api.get('/branches')
      .then(r => setBranches(r.data || []))
      .catch(() => toast.error('Could not load branches'));
  }, []);

  /* ── Step Navigation ── */
  const handleNext = () => {
    if (activeStep === 0 && !branchId) {
      toast.error('Please select a branch first');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start Date cannot be greater than End Date');
      return;
    }
    setActiveStep(s => s + 1);
  };

  const handleBack = () => {
    setActiveStep(s => s - 1);
    setFile(null);
    setPreview(null);
    setSaveResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /* ── STEP 1: Download Dynamic Date Boundary Template ── */
  const downloadTemplate = async () => {
    try {
      const res = await api.get(
        `/attendance/manual/template/${branchId}?startDate=${startDate}&endDate=${endDate}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_template_${startDate}_to_${endDate}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded!');
      setActiveStep(2); // Auto advance to upload dropzone
    } catch (e) {
      toast.error('Download failed: ' + (e?.response?.data?.message || e.message));
    }
  };

  /* ── STEP 2: Drag & Drop + File Validation ── */
  const validateAndSetFile = (selectedFile) => {
    if (!selectedFile) return;
    if (!selectedFile.name.endsWith('.xlsx')) {
      toast.error('Please upload a valid Excel (.xlsx) file');
      return;
    }
    setFile(selectedFile);
    setPreview(null);
    setSaveResult(null);
  };

  const handleFileChange = (e) => {
    validateAndSetFile(e.target.files[0]);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handlePreview = async () => {
    if (!file) { toast.error('Please select a file first'); return; }
    setLoadingPreview(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/attendance/manual/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreview(res.data);
      setActiveStep(3);
      toast.success(`${res.data.length} records parsed for preview`);
    } catch (e) {
      toast.error('Preview failed: ' + (e?.response?.data?.message || e.message));
    } finally {
      setLoadingPreview(false);
    }
  };

  /* ── STEP 3: Safe Confirm and Commit execution ── */
  const handleConfirm = async () => {
    if (!file) { toast.error('No file loaded to confirm.'); return; }
    setLoadingSave(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/attendance/manual/confirm', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSaveResult(res.data);
      toast.success(`Saved ${res.data.saved} records securely.`);
    } catch (e) {
      toast.error('Save failed: ' + (e?.response?.data?.message || e.message));
    } finally {
      setLoadingSave(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setFile(null);
    setPreview(null);
    setSaveResult(null);
    setBranchId('');
    setStartDate(getTodayString());
    setEndDate(getTodayString());
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /* ── RENDER LAYER ── */
  return (
    <Box sx={{ p: 3 }}>
      {/* Title Header Layout */}
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Dynamic Attendance Upload
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Select custom payroll cycles or a single target date. Download the template displaying those exact date parameters as columns, complete changes, and re-upload.
      </Typography>

      {/* Stepper Pipeline tracker */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map(label => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {/* ── STEP 0: Select dynamic parameters ── */}
      {activeStep === 0 && (
        <Paper sx={{ p: 3 }} elevation={1}>
          <Typography variant="h6" gutterBottom>Select Branch & Target Window</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth required>
                <InputLabel>Branch</InputLabel>
                <Select value={branchId} label="Branch" onChange={e => setBranchId(e.target.value)}>
                  {branches.map(b => (
                    <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                label="Start Date" type="date" fullWidth
                value={startDate} onChange={e => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                label="End Date" type="date" fullWidth
                value={endDate} onChange={e => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Box mt={3}>
            <Button variant="contained" onClick={handleNext} disabled={!branchId} size="large">
              Next Step
            </Button>
          </Box>
        </Paper>
      )}

      {/* ── STEP 1: Download structured layout ── */}
      {activeStep === 1 && (
        <Paper sx={{ p: 3 }} elevation={1}>
          <Typography variant="h6" gutterBottom>Download Attendance Template</Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            Selected Branch: <strong>{branches.find(b => b.id === branchId)?.name}</strong> &nbsp;|&nbsp;
            Selected Window: <strong>{startDate}</strong> to <strong>{endDate}</strong> ({startDate === endDate ? "Single Day Correction Run" : "Custom Payroll Range Run"})
          </Alert>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={handleBack}>Back</Button>
            <Button
              variant="contained" startIcon={<Download />}
              onClick={downloadTemplate}
              color="primary"
            >
              Download Template
            </Button>
            <Button variant="text" onClick={() => setActiveStep(2)}>
              Skip to Upload Step
            </Button>
          </Stack>
        </Paper>
      )}

      {/* ── STEP 2: Upload file dropzone ── */}
      {activeStep === 2 && (
        <Paper sx={{ p: 3 }} elevation={1}>
          <Typography variant="h6" gutterBottom>Upload Filled Template</Typography>
          <Typography variant="body2" mb={2} color="text.secondary">
            Select or drag in the modified <code>.xlsx</code> workbook template matching your parameters.
          </Typography>

          <Box
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : file ? 'success.main' : 'grey.400',
              borderRadius: 2,
              p: 5,
              textAlign: 'center',
              cursor: 'pointer',
              mb: 3,
              backgroundColor: isDragActive ? 'action.hover' : 'inherit',
              transition: 'all 0.2s ease',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
            }}
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
          >
            <CloudUpload sx={{ fontSize: 56, color: file ? 'success.main' : 'grey.400', mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={500}>
              {file ? `Target Selected: ${file.name}` : 'Drag & drop your excel file here, or click to browse'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Supports only Microsoft Excel Worksheet (.xlsx) assets
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              hidden
              onChange={handleFileChange}
            />
          </Box>

          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={handleBack}>Back</Button>
            <Button
              variant="contained"
              startIcon={loadingPreview ? <CircularProgress size={18} color="inherit" /> : <Visibility />}
              onClick={handlePreview}
              disabled={!file || loadingPreview}
            >
              {loadingPreview ? 'Parsing Elements…' : 'Generate Preview'}
            </Button>
          </Stack>
        </Paper>
      )}

      {/* ── STEP 3: In-Memory Validation Preview Matrix ── */}
      {activeStep === 3 && preview && (
        <Paper sx={{ p: 3 }} elevation={1}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Parsing Evaluation Summary — {preview.length} Records Detected
          </Typography>

          {preview.some(r => !r.valid) && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <strong>{preview.filter(r => !r.valid).length} rows contain logical evaluation issues</strong> and will be auto-skipped during persistence operations. Review structural flags inside the table grid layout below.
            </Alert>
          )}

          {saveResult ? (
            /* ── POST-SAVE PRODUCTION ATOMIC REPORT ── */
            <Box py={2}>
              <Alert
                severity={saveResult.errors > 0 ? 'warning' : 'success'}
                sx={{ mb: 3 }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Data Commit Execution Run Finished
                </Typography>
                <Typography variant="body2">
                  Successfully stored: <strong>{saveResult.saved}</strong> changes. Fault exceptions: {saveResult.errors}.
                </Typography>
              </Alert>
              <Button variant="contained" onClick={handleReset} size="large">
                Upload New Date Bounds
              </Button>
            </Box>
          ) : (
            <>
              {/* ── PREVIEW DATA GRID TABLE ── */}
              <TableContainer sx={{ maxHeight: 440, mb: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Code</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Employee Name</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Log Date</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Raw Input</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>In Punch</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Out Punch</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Status Badge</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }} align="center">Passed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No active parameters modified. Ensure cells inside the template are populated correctly.
                        </TableCell>
                      </TableRow>
                    ) : (
                      preview.map((row, idx) => (
                        <TableRow
                          key={idx}
                          hover
                          sx={{ bgcolor: row.valid ? 'inherit' : 'error.50', transition: 'background 0.2s' }}
                        >
                          <TableCell>{row.employeeCode}</TableCell>
                          <TableCell>{row.employeeName}</TableCell>
                          <TableCell>{row.date}</TableCell>
                          <TableCell><code style={{ fontSize: '11px' }}>{row.rawValue}</code></TableCell>
                          <TableCell>{row.clockIn || '—'}</TableCell>
                          <TableCell>{row.clockOut || '—'}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.displayStatus || '—'}
                              color={statusColor(row.displayStatus)}
                              size="small"
                              sx={{ fontWeight: 'bold', borderRadius: '4px' }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {row.valid ? (
                              <CheckCircle color="success" fontSize="small" />
                            ) : (
                              <Tooltip title={row.error || 'Syntax parsing runtime mismatch'}>
                                <Cancel color="error" fontSize="small" style={{ cursor: 'help' }} />
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ mb: 3 }} />

              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={handleBack} disabled={loadingSave}>
                  Cancel / Modify File
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={loadingSave ? <CircularProgress size={18} color="inherit" /> : <Save />}
                  onClick={handleConfirm}
                  disabled={loadingSave || preview.filter(r => r.valid).length === 0}
                  size="large"
                >
                  {loadingSave ? 'Saving Records…' : `Confirm & Save ${preview.filter(r => r.valid).length} Rows`}
                </Button>
              </Stack>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default HRManualAttendanceUpload;